/**
 * El extracto de un proveedor: el libro mayor, del más viejo al más nuevo.
 *
 * EL ORDEN NO SE NEGOCIA. La columna que importa es el saldo acumulado, y un
 * acumulado en orden invertido no acumula nada.
 *
 * LO QUE ESTA VUELTA AGREGA, Y POR QUÉ
 * ------------------------------------
 * Los dos datos que contestan «de dónde viene la deuda» estaban escritos en la
 * base desde 0062 y ninguna pantalla los leía:
 *
 *   · `compra_id` — el renglón de una compra ahora enlaza al remito con sus
 *     líneas. Es literalmente de dónde viene esa deuda.
 *   · las imputaciones — el renglón dice si la factura quedó pagada, y si no,
 *     cuánto se pagó de cuánto. El FIFO decidía y no se veía qué decidió.
 *
 * EL ESTADO VA EN EL RENGLÓN DE LA FACTURA, no en el del pago, porque así llega
 * la pregunta: el proveedor llama por la 42, no por el pago del martes.
 *
 * Y va con badge y con texto, nunca con el color del importe: el número lleva
 * el color de su moneda (MASTER §3.4).
 */
import { Link } from "@tanstack/react-router";
import { Banknote, ExternalLink } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Cargando } from "@/components/ui/cargando";
import { EstadoVacio } from "@/components/ui/estado-vacio";
import {
  Table,
  TableBody,
  TableCell,
  TableCellImporte,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TituloSeccion } from "@/components/ui/titulo-seccion";
import { Importe } from "@/components/money/importe";
import { aNumero } from "@/lib/tipos";

import {
  ETIQUETA_TIPO_PROVEEDOR,
  detalleDelPago,
  estadoDePago,
  fechaDeMovimiento,
  type DeudaProveedor,
  type EstadoPagoDeuda,
  type MovimientoProveedor,
} from "./logica";

/** Cómo se lee cada tipo de movimiento. Vive en `logica`: lo usan tambien las deudas. */
const ETIQUETA = ETIQUETA_TIPO_PROVEEDOR;

export interface ExtractoProveedorProps {
  movimientos: ReadonlyArray<MovimientoProveedor>;
  /** Todas las deudas, abiertas y cerradas (`/deudas?todas=1`). */
  deudas: ReadonlyArray<DeudaProveedor>;
  cargando?: boolean;
}

export function ExtractoProveedor({
  movimientos,
  deudas,
  cargando = false,
}: ExtractoProveedorProps): JSX.Element {
  if (movimientos.length === 0) {
    return (
      <div className="space-y-2">
        <TituloSeccion>Todo lo que pasó</TituloSeccion>
        {cargando ? (
          <Card padding="none">
            <Cargando texto="Buscando…" />
          </Card>
        ) : (
          <EstadoVacio
            icono={Banknote}
            titulo="Sin movimientos"
            detalle="Se llena solo cuando apliques una compra de este proveedor. Si ya le debías algo de antes, cargalo con Ajustar."
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <TituloSeccion>Todo lo que pasó</TituloSeccion>
      <Card padding="none" className="overflow-hidden">
        <Table densidad="compacta" alto="acotado">
          <TableHeader>
            <TableRow>
              <TableHead>Fecha</TableHead>
              <TableHead>Qué</TableHead>
              <TableHead className="text-right">Le debo</TableHead>
              <TableHead className="text-right">Le pagué</TableHead>
              <TableHead className="text-right">Saldo</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {movimientos.map((m) => (
              <Fila key={m.id} movimiento={m} estado={estadoDePago(m.id, deudas)} />
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

function Fila({
  movimiento: m,
  estado,
}: {
  movimiento: MovimientoProveedor;
  estado: EstadoPagoDeuda | null;
}): JSX.Element {
  const acumulado = aNumero(m.moneda === "USD" ? m.saldo_acumulado_usd : m.saldo_acumulado_ars);
  const debe = aNumero(m.deuda);
  const pago = aNumero(m.pago);
  // La nota que se escribio al cargarlo («por la unidad que no vino»). Las
  // compras la repiten igual a la referencia, un ajuste sin nota trae el tipo
  // y un pago sin nota dice «Pago a <este proveedor>»: nada de eso es nuevo.
  const nota =
    m.detalle &&
    m.detalle !== m.referencia &&
    m.detalle !== m.tipo &&
    !(m.tipo === "PAGO" && m.detalle.startsWith("Pago a "))
      ? m.detalle
      : null;
  // De qué caja salió el pago y, si era de otra moneda, cuánto y a qué dólar.
  const conQue = detalleDelPago(m);

  return (
    <TableRow>
      <TableCell className="whitespace-nowrap text-xs tabular-nums text-muted-foreground">
        {fechaDeMovimiento(m.fecha)}
      </TableCell>
      <TableCell className="text-sm">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline">{ETIQUETA[m.tipo]}</Badge>
          {/* El link al remito: de dónde viene esta deuda. */}
          {m.compra_id ? (
            <Link
              to="/compras"
              search={{ id: m.compra_id }}
              className="inline-flex items-center gap-1 text-xs text-primary underline-offset-4 hover:underline"
            >
              {m.referencia ?? "Ver el remito"}
              <ExternalLink aria-hidden="true" className="size-3" />
            </Link>
          ) : m.referencia ? (
            <span className="truncate text-xs text-muted-foreground">{m.referencia}</span>
          ) : null}
          {estado ? <EstadoDePago estado={estado} moneda={m.moneda} /> : null}
        </div>
        {conQue ? <p className="mt-0.5 text-xs text-muted-foreground">{conQue}</p> : null}
        {nota ? <p className="mt-0.5 text-xs text-muted-foreground">{nota}</p> : null}
      </TableCell>
      {/* Con `monto` la celda pinta el importe y descarta los children; sin él,
          los usa. Es como se escribe un "—" sin repetir la celda entera. */}
      <TableCellImporte {...(debe > 0 ? { monto: debe, moneda: m.moneda } : {})} tamano="sm">
        <span className="text-xs text-muted-foreground">—</span>
      </TableCellImporte>
      <TableCellImporte {...(pago > 0 ? { monto: pago, moneda: m.moneda } : {})} tamano="sm">
        <span className="text-xs text-muted-foreground">—</span>
      </TableCellImporte>
      <TableCellImporte monto={acumulado} moneda={m.moneda} tamano="sm" />
    </TableRow>
  );
}

/**
 * Cómo quedó esta factura.
 *
 * `Badge` para lo cerrado, texto chico para lo que va a medias. Nunca el color
 * del importe: el número lleva el color de su moneda.
 */
function EstadoDePago({
  estado,
  moneda,
}: {
  estado: EstadoPagoDeuda;
  moneda: MovimientoProveedor["moneda"];
}): JSX.Element {
  if (estado.clase === "PAGADA") {
    return <Badge variant="secondary">pagada</Badge>;
  }
  if (estado.clase === "PARCIAL") {
    return (
      <span className="flex items-center gap-1 text-xs text-muted-foreground">
        <Importe monto={estado.pagado} moneda={moneda} tamano="sm" codigo={false} /> de{" "}
        <Importe monto={estado.total} moneda={moneda} tamano="sm" codigo={false} />
      </span>
    );
  }
  return <span className="text-xs text-muted-foreground">sin pagar</span>;
}
